import moment from "moment";
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import {
  Form,
  Row,
  Table,
  Col,
  Spin,
  Input,
  Button,
  message,
  Tag,
  Select,
  Space,
  Grid,
  Typography,
  Card,
  Drawer,
  Badge,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { FilterDropdownProps } from "antd/es/table/interface";

import { useClient } from "hooks/useClient";
import {
  getEventsByQueryParams,
  EventResponseDto,
  getEventTypes,
  EventType,
  getAllDistributionPoints,
  DistributionPointResponseResource,
  downloadEventsReport,
} from "api/generated";
import { useDateRange } from "context/DateRangeContext";
import {
  DateFilterType,
  DateRangePicker,
} from "common/components/widgets/datePicker/DateRangePicker";
import { usePaginationTable } from "hooks/commons/usePaginationTable";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { querySerializer } from "common/helpers/queryUtils";
import {
  handleAxiosError,
  handleCsvDownload,
} from "common/helpers/generalUtils";

const { Option } = Select;
const { useBreakpoint } = Grid;
const { Text } = Typography;

interface DistributionPointFilterProps {
  distributionPoints: DistributionPointResponseResource[];
  selectedId: string | undefined;
  onSelect: (id: string | undefined) => void;
  onClear: () => void;
}

function DistributionPointFilter({
  distributionPoints,
  selectedId,
  onSelect,
  onClear,
}: DistributionPointFilterProps) {
  const [search, setSearch] = useState("");
  const filtered = distributionPoints.filter((dp) =>
    (dp.name ?? "").toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div style={{ padding: 8, width: 250 }}>
      <Input
        placeholder="Buscar punto de distribución..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        prefix={<SearchOutlined />}
        allowClear
        autoFocus
        style={{ marginBottom: 8 }}
      />
      <div style={{ maxHeight: 200, overflowY: "auto", marginBottom: 8 }}>
        <div
          onClick={() => onSelect(undefined)}
          style={{
            padding: "4px 8px",
            cursor: "pointer",
            fontWeight: !selectedId ? "bold" : "normal",
            background: !selectedId ? "#e6f4ff" : "transparent",
            borderRadius: 4,
          }}
        >
          Todos
        </div>
        {filtered.map((dp) => {
          const id = String(dp.id);
          const isSelected = selectedId === id;
          return (
            <div
              key={id}
              onClick={() => onSelect(id)}
              style={{
                padding: "4px 8px",
                cursor: "pointer",
                fontWeight: isSelected ? "bold" : "normal",
                background: isSelected ? "#e6f4ff" : "transparent",
                borderRadius: 4,
              }}
            >
              {dp.name ?? id}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ padding: "4px 8px", color: "#aaa" }}>
            Sin resultados
          </div>
        )}
      </div>
      <Space>
        <Button size="small" onClick={onClear}>
          Limpiar
        </Button>
      </Space>
    </div>
  );
}

const CARD_HEIGHT = 92;
const PAGINATION_BAR = 44;
const MIN_PAGE_SIZE = 10;

function useMobilePageSize(
  containerRef: React.RefObject<HTMLDivElement | null>,
): number {
  const [pageSize, setPageSize] = useState(MIN_PAGE_SIZE);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const calculate = () => {
      const available = el.getBoundingClientRect().height - PAGINATION_BAR;
      const count = Math.max(
        MIN_PAGE_SIZE,
        Math.floor(available / CARD_HEIGHT),
      );
      setPageSize(count);
    };

    calculate();

    const observer = new ResizeObserver(calculate);
    observer.observe(el);
    return () => observer.disconnect();
  }, [containerRef]);

  return pageSize;
}

interface MobileEventListProps {
  items: EventResponseDto[];
  loading: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, size: number) => void;
  };
}

function MobileEventList({
  items,
  loading,
  containerRef,
  pagination,
}: MobileEventListProps) {
  const totalPages = Math.ceil(pagination.total / pagination.pageSize);

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        minHeight: 0,
      }}
    >
      <Spin spinning={loading} tip="Cargando..." style={{ flex: 1 }}>
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            overflow: "hidden",
          }}
        >
          {items.length === 0 && !loading && (
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#999",
                fontSize: 13,
              }}
            >
              Sin eventos en el periodo seleccionado
            </div>
          )}

          {items.map((event, idx) => {
            const folio = event.eventFolio ?? "-";
            const fecha = event.eventDate
              ? moment(event.eventDate).format("DD/MM/YY HH:mm")
              : "-";
            const tipo = event.eventType ?? "-";
            const descripcion = event.eventDescription ?? "-";
            const punto = event.distributionPointName;
            const key = event.id?.toString() ?? idx;

            return (
              <Card
                key={key}
                size="small"
                styles={{ body: { padding: "10px 12px" } }}
                style={{
                  borderRadius: 8,
                  border: "1px solid #e8e8e8",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 6,
                  }}
                >
                  <Text strong style={{ fontSize: 13, color: "#1677ff" }}>
                    #{folio}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {fecha}
                  </Text>
                </div>

                <Text
                  style={{ fontSize: 12, display: "block", marginBottom: 6 }}
                  ellipsis={{ tooltip: descripcion }}
                >
                  {descripcion}
                </Text>

                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>
                    {tipo}
                  </Tag>
                  {punto ? (
                    <Tag color="geekblue" style={{ fontSize: 11, margin: 0 }}>
                      {punto}
                    </Tag>
                  ) : (
                    <Tag color="default" style={{ fontSize: 11, margin: 0 }}>
                      Global
                    </Tag>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </Spin>

      {pagination.total > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: 8,
            borderTop: "1px solid #f0f0f0",
            marginTop: 8,
            flexShrink: 0,
          }}
        >
          <Button
            size="small"
            disabled={pagination.current <= 1 || loading}
            onClick={() =>
              pagination.onChange(pagination.current - 1, pagination.pageSize)
            }
          >
            ← Anterior
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Pág. {pagination.current} / {totalPages}
            <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
              ({pagination.total} eventos)
            </Text>
          </Text>
          <Button
            size="small"
            disabled={pagination.current >= totalPages || loading}
            onClick={() =>
              pagination.onChange(pagination.current + 1, pagination.pageSize)
            }
          >
            Siguiente →
          </Button>
        </div>
      )}
    </div>
  );
}

function EventPage(props: RouteComponentProps) {
  const [form] = Form.useForm();
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingEventTypes, setLoadingEventTypes] = useState(false);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [distributionPoints, setDistributionPoints] = useState<
    DistributionPointResponseResource[]
  >([]);

  const [eventTypeId, setEventTypeId] = useState<string | undefined>();
  const [eventDescription, setEventDescription] = useState<
    string | undefined
  >();
  const [selectedDistributionId, setSelectedDistributionId] = useState<
    string | undefined
  >();

  const [drawerOpen, setDrawerOpen] = useState(false);

  const cardContainerRef = useRef<HTMLDivElement>(null);

  const { client } = useClient();
  const { rangeTimeValue, setRangeTimeValue, getFormattedDates } =
    useDateRange();
  const { startDate, endDate } = getFormattedDates({
    startDateFormat: "YYYY-MM-DD",
    endDateFormat: "YYYY-MM-DD",
  });

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const mobilePageSize = useMobilePageSize(cardContainerRef);

  const pagination = usePaginationTable<EventResponseDto>(props, {
    persistInUrl: true,
  });

  const activeFilters = [
    eventTypeId,
    eventDescription,
    selectedDistributionId,
  ].filter(Boolean).length;

  const handleDownload = () => {
    const eventReq = {
      initDate: startDate,
      endDate: endDate,
      eventDescription,
      eventTypeId,
      distributionId: selectedDistributionId,
    };
    setLoadingEvents(true);
    message.loading("Preparando descarga...", 1.5);
    downloadEventsReport({
      client,
      body: eventReq,
      path: {
        type: "csv",
      },
    })
      .then((data) => {
        handleCsvDownload(data);
      })
      .catch((err) =>
        handleAxiosError(err, "No se ha podido descargar el archivo"),
      )
      .finally(() => {
        setLoadingEvents(false);
      });
  };

  useEffect(() => {
    setLoadingEventTypes(true);
    getEventTypes({ client })
      .then(({ data }) => setEventTypes(data ?? []))
      .catch((err) =>
        handleAxiosError(err, "No se pudieron obtener los tipos de eventos"),
      )
      .finally(() => setLoadingEventTypes(false));

    getAllDistributionPoints({ client })
      .then(({ data }) => setDistributionPoints(data ?? []))
      .catch((err) =>
        handleAxiosError(
          err,
          "No se pudieron obtener los puntos de distribución",
        ),
      );
  }, [client]);

  const fetchEvents = useCallback(() => {
    setLoadingEvents(true);

    getEventsByQueryParams({
      client,
      querySerializer,
      query: {
        request: {
          initDate: startDate,
          endDate,
          eventDescription,
          eventTypeId,
          distributionId: selectedDistributionId,
        },
        pageable: {
          page: pagination.currentPage,
          size: isMobile ? mobilePageSize : pagination.pageSize,
          sort: undefined,
        },
      },
    })
      .then(({ data }) => {
        pagination.setTotalItems(data?.totalElements ?? 0);
        pagination.setItems(data?.content ?? []);
      })
      .catch(() => message.error("No se pudieron cargar los eventos"))
      .finally(() => setLoadingEvents(false));
  }, [
    client,
    startDate,
    endDate,
    eventDescription,
    eventTypeId,
    selectedDistributionId,
    pagination.currentPage,
    pagination.pageSize,
    isMobile,
    mobilePageSize,
  ]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleEventTypeChange = (value: string) =>
    setEventTypeId(value || undefined);

  const handleSearch = () => {
    const description = form.getFieldValue("eventDescription");
    setEventDescription(description || undefined);
    setDrawerOpen(false);
  };

  const handleReset = () => {
    form.resetFields();
    setEventTypeId(undefined);
    setEventDescription(undefined);
    setSelectedDistributionId(undefined);
    setDrawerOpen(false);
  };

  const columns: ColumnsType<EventResponseDto> = [
    { title: "Folio", dataIndex: "eventFolio", width: "7%" },
    {
      title: "Fecha evento",
      dataIndex: "eventDate",
      width: "13%",
      render: (date: string) =>
        date ? moment(date).format("YYYY/MM/DD HH:mm:ss") : "-",
    },
    {
      title: "Descripción",
      dataIndex: "eventDescription",
      width: "20%",
      render: (val: string) => val ?? "-",
    },
    {
      title: "Origen del evento",
      dataIndex: "eventOrigin",
      width: "10%",
      render: (val: string) => val ?? "-",
    },
    {
      title: "Componente alarma",
      dataIndex: "alarmComponent",
      width: "12%",
      render: (val: string) => val ?? "-",
    },
    {
      title: "IP origen",
      dataIndex: "originIp",
      width: "10%",
      render: (val: string) => val ?? "-",
    },
    {
      title: "Tipo de Evento",
      dataIndex: "eventType",
      width: "10%",
      render: (val: string) => val ?? "-",
    },
    {
      title: "Punto de distribución",
      dataIndex: "distributionPointName",
      width: "15%",
      render: (val: string) => val ?? <Tag color="default">Global</Tag>,
      filteredValue: selectedDistributionId ? [selectedDistributionId] : null,
      onFilter: () => true,
      filterDropdown: ({ confirm, clearFilters }: FilterDropdownProps) => (
        <DistributionPointFilter
          distributionPoints={distributionPoints}
          selectedId={selectedDistributionId}
          onSelect={(id) => {
            setSelectedDistributionId(id);
            confirm({ closeDropdown: true });
          }}
          onClear={() => {
            setSelectedDistributionId(undefined);
            clearFilters?.();
            confirm({ closeDropdown: true });
          }}
        />
      ),
      filterIcon: () => (
        <SearchOutlined
          style={{ color: selectedDistributionId ? "#1890ff" : undefined }}
        />
      ),
    },
  ];

  const filterContent = (
    <Form form={form} layout="vertical">
      <Form.Item label="Tipo de evento" name="eventTypeId" initialValue="">
        <Select
          onChange={handleEventTypeChange}
          loading={loadingEventTypes}
          allowClear
          showSearch
          placeholder="Todos"
          optionFilterProp="children"
          size={isMobile ? "small" : "middle"}
        >
          <Option value="">Todos</Option>
          {eventTypes.map((type) => (
            <Option key={type.key} value={String(type.key)}>
              {type.value}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item label="Descripción del evento" name="eventDescription">
        <Input
          placeholder="Buscar por descripción..."
          allowClear
          onPressEnter={handleSearch}
        />
      </Form.Item>

      <Form.Item label="Seleccionar periodo">
        <DateRangePicker
          config={{
            allowedTypes: [
              DateFilterType.DAY,
              DateFilterType.MONTH,
              DateFilterType.RANGE,
            ],
            defaultType: DateFilterType.MONTH,
            showQuickFilters: true,
          }}
          rangeTimeValue={rangeTimeValue}
          setRangeTimeValue={setRangeTimeValue}
        />
      </Form.Item>

      {isMobile && (
        <Form.Item label="Punto de distribución" name="distributionPointId">
          <Select
            allowClear
            showSearch
            placeholder="Todos"
            optionFilterProp="label"
            value={selectedDistributionId}
            onChange={(val) => setSelectedDistributionId(val ?? undefined)}
            onClear={() => setSelectedDistributionId(undefined)}
            options={[
              { label: "Todos", value: undefined },
              ...distributionPoints.map((dp) => ({
                label: dp.name ?? String(dp.id),
                value: String(dp.id),
              })),
            ]}
          />
        </Form.Item>
      )}

      <Row gutter={8}>
        <Col>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
            disabled={loadingEvents}
          >
            Buscar
          </Button>
        </Col>
        <Col>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleReset}
            disabled={loadingEvents}
          >
            Limpiar
          </Button>
        </Col>
      </Row>
    </Form>
  );

  return (
    <Spin
      size="large"
      tip="Cargando..."
      spinning={loadingEvents && isMobile ? false : loadingEvents}
    >
      {isMobile ? (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
                flexShrink: 0,
              }}
            >
              <Text strong style={{ fontSize: 15 }}>
                Eventos
              </Text>
              <Space>
                <Button icon={<DownloadOutlined />} onClick={handleDownload} />
                <Badge count={activeFilters} size="small">
                  <Button
                    icon={<FilterOutlined />}
                    onClick={() => setDrawerOpen(true)}
                    size="middle"
                  >
                    Filtros
                  </Button>
                </Badge>
              </Space>
            </div>

            <MobileEventList
              items={pagination.items}
              loading={loadingEvents}
              containerRef={cardContainerRef}
              pagination={{
                current: pagination.currentPage + 1,
                pageSize: mobilePageSize,
                total: pagination.totalItems ?? 0,
                onChange: (page, size) => pagination.onPageChange(page, size),
              }}
            />
          </div>

          <Drawer
            title="Filtros"
            placement="bottom"
            height="auto"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            styles={{ body: { paddingBottom: 40 } }}
          >
            {filterContent}
          </Drawer>
        </>
      ) : (
        <>
          <Form form={form} layout="vertical">
            <Row gutter={[16, 12]} align="bottom" wrap>
              <Col xs={24} sm={12} lg={5}>
                <Form.Item
                  label="Tipo de evento"
                  name="eventTypeId"
                  initialValue=""
                >
                  <Select
                    onChange={handleEventTypeChange}
                    loading={loadingEventTypes}
                    allowClear
                    showSearch
                    placeholder="Todos"
                    optionFilterProp="children"
                  >
                    <Option value="">Todos</Option>
                    {eventTypes.map((type) => (
                      <Option key={type.key} value={String(type.key)}>
                        {type.value}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={5}>
                <Form.Item
                  label="Descripción del evento"
                  name="eventDescription"
                >
                  <Input
                    placeholder="Buscar por descripción..."
                    allowClear
                    onPressEnter={handleSearch}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} lg={8}>
                <Form.Item label="Seleccionar periodo">
                  <DateRangePicker
                    config={{
                      allowedTypes: [
                        DateFilterType.DAY,
                        DateFilterType.MONTH,
                        DateFilterType.RANGE,
                      ],
                      defaultType: DateFilterType.MONTH,
                      showQuickFilters: true,
                    }}
                    rangeTimeValue={rangeTimeValue}
                    setRangeTimeValue={setRangeTimeValue}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} lg={6}>
                <Form.Item className="transparent-label" label=" ">
                  <Space wrap>
                    <Button
                      type="primary"
                      icon={<SearchOutlined />}
                      onClick={handleSearch}
                      disabled={loadingEvents}
                    >
                      Buscar
                    </Button>
                    <Button onClick={handleReset} disabled={loadingEvents}>
                      Limpiar
                    </Button>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={handleDownload}
                      disabled={loadingEvents}
                    >
                      Descargar
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <Table<EventResponseDto>
            columns={columns}
            scroll={{ x: true }}
            rowKey={(record) =>
              record.id?.toString() ?? Math.random().toString()
            }
            dataSource={pagination.items}
            pagination={{
              ...pagination.paginationConfig,
              onChange: pagination.onPageChange,
              onShowSizeChange: pagination.onPageSizeChange,
            }}
            loading={loadingEvents}
          />
        </>
      )}
    </Spin>
  );
}

export default withRouter(EventPage);
